import {
  floorShares,
  maxTilesForArea,
  talkOfTheTown,
  tileShares,
  type BoardPost,
  type SizedTile,
  type SizingInput,
} from '@talkofthetown/shared';
import {
  hierarchy,
  treemap,
  treemapBinary,
  treemapSquarify,
  type HierarchyRectangularNode,
} from 'd3-hierarchy';

export interface TileRect {
  post: BoardPost;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BoardLayout {
  tiles: TileRect[];
  /** The talk of the town: the post opened most, on the biggest tile (none while all are new). */
  crown: string | null;
  /** Posts that couldn't get a readable tile (too many for the board), least opened first. */
  dropped: BoardPost[];
}

interface Node {
  id?: string;
  value?: number;
  children?: Node[];
}

/** Phones: a squarified treemap leaves slivers too thin for words, so narrow boards use rows. */
const ROWS_BELOW_PX = 560;
const MIN_TILE_WIDTH = 110;
const MIN_ROW_HEIGHT = 84;

const narrowestIn = (row: readonly SizedTile[], width: number) =>
  (width * Math.min(...row.map((t) => t.share))) / row.reduce((a, t) => a + t.share, 0);

/**
 * Groups tiles (largest first) into rows aiming at roughly square tiles, none narrower than
 * MIN_TILE_WIDTH, and without leaving a lone tile stretched across the last row.
 */
export function groupRows(
  shares: readonly SizedTile[],
  width: number,
  height: number,
): SizedTile[][] {
  const side = Math.sqrt((width * height) / Math.max(1, shares.length));
  const perRow = Math.max(
    1,
    Math.min(Math.round(width / side), Math.floor(width / MIN_TILE_WIDTH)),
  );
  const rows: SizedTile[][] = [];
  let row: SizedTile[] = [];
  for (const tile of shares) {
    const next = [...row, tile];
    if (row.length > 0 && (next.length > perRow || narrowestIn(next, width) < MIN_TILE_WIDTH)) {
      rows.push(row);
      row = [tile];
    } else row = next;
  }
  if (row.length > 0) rows.push(row);
  const last = rows[rows.length - 1]!;
  const prev = rows[rows.length - 2];
  if (
    prev &&
    last.length === 1 &&
    perRow > 1 &&
    narrowestIn([...prev, ...last], width) >= MIN_TILE_WIDTH
  ) {
    prev.push(...rows.pop()!);
  }
  return rows;
}

/** Splits `total` px into whole-pixel [start, length] spans following `weights`, `gap` px apart. */
function split(weights: readonly number[], total: number, gap: number): Array<[number, number]> {
  const free = total - gap * (weights.length - 1);
  const sum = weights.reduce((a, b) => a + b, 0);
  let acc = 0;
  return weights.map((w, i) => {
    const start = Math.round((acc / sum) * free) + gap * i;
    acc += w;
    return [start, Math.round((acc / sum) * free) + gap * i - start];
  });
}

/** The biggest tile's row (the talk of the town) is at least this tall, when there's room. */
const FIRST_ROW_HEIGHT = 150;

/**
 * Rows: a row's height follows its share (at least MIN_ROW_HEIGHT when there's room) and a tile's
 * width its share of the row. Bigger still means more attention, and every tile stays wide and tall
 * enough for a few words. On a narrow screen a share of the board is a thin strip, so the biggest
 * row gets room for the talk of the town's badge and a few lines of title, and sits in the middle
 * of the board. The talk of the town (`crown`), when given, gets that row to itself.
 */
function rowsLayout(
  shares: readonly SizedTile[],
  width: number,
  height: number,
  gap: number,
  crown: string | null = null,
) {
  const alone = crown ? shares.find((s) => s.id === crown) : undefined;
  let rows = alone
    ? [
        [alone],
        ...groupRows(
          shares.filter((s) => s !== alone),
          width,
          height,
        ),
      ]
    : groupRows(shares, width, height);
  const rowShares = rows.map((r) => r.reduce((a, t) => a + t.share, 0));
  const free = height - gap * (rows.length - 1);
  const floored = floorShares(rowShares, MIN_ROW_HEIGHT / free);
  const sum = floored.reduce((a, b) => a + b, 0);
  let heights = floored.map((h) => (h / sum) * free);
  const first = Math.min(FIRST_ROW_HEIGHT, free - (rows.length - 1) * MIN_ROW_HEIGHT);
  if (rows.length > 1 && heights[0]! < first) {
    const rest = (free - first) / (free - heights[0]!);
    heights = heights.map((h, i) => (i === 0 ? first : h * rest));
  }
  const middle = Math.floor((rows.length - 1) / 2);
  const toMiddle = <T>(list: T[]) => [
    ...list.slice(1, middle + 1),
    list[0]!,
    ...list.slice(middle + 1),
  ];
  rows = toMiddle(rows);
  heights = toMiddle(heights);
  return split(heights, height, gap).flatMap(([y, h], r) => {
    const row = rows[r]!;
    return split(
      row.map((t) => t.share),
      width,
      gap,
    ).map(([x, w], i) => ({ id: row[i]!.id, x, y, w, h }));
  });
}

/**
 * A bubble smaller than this can't show a readable line of title (padding and tail take room), so
 * its post doesn't get one.
 */
const READABLE_W = 110;
const READABLE_H = 80;
const readable = (r: { w: number; h: number }) => r.w >= READABLE_W && r.h >= READABLE_H;

const sizing = (p: BoardPost): SizingInput => ({
  id: p.id,
  score: p.score,
  isAgent: p.isAgent,
  fresh: p.fresh,
  createdAt: p.createdAt,
});

/** Biggest first (ties by id, so the order is steady). */
function sharesFor(items: readonly SizingInput[], width: number, height: number) {
  return tileShares(items, width * height).sort(
    (a, b) => b.share - a.share || a.id.localeCompare(b.id),
  );
}

type Tiling = (
  node: HierarchyRectangularNode<Node>,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
) => void;

/** Treemap arrangements to try, best-looking first; the rows layout is the last resort. */
const TILINGS: Tiling[] = [treemapSquarify, treemapSquarify.ratio(1), treemapBinary];

function treemapped(
  shares: readonly SizedTile[],
  width: number,
  height: number,
  gap: number,
  tiling: Tiling,
) {
  const root = hierarchy<Node>({
    children: shares.map((s) => ({ id: s.id, value: s.share })),
  }).sum((d) => d.value ?? 0);
  const laid = treemap<Node>().size([width, height]).tile(tiling).paddingInner(gap).round(true)(
    root,
  );
  return laid.leaves().map((leaf) => ({
    id: leaf.data.id!,
    x: leaf.x0,
    y: leaf.y0,
    w: Math.max(0, leaf.x1 - leaf.x0),
    h: Math.max(0, leaf.y1 - leaf.y0),
  }));
}

/** A treemap of `shares` (any total) in a box at (x, y), or null if a tile can't be readable. */
function treemapIn(
  shares: readonly SizedTile[],
  x: number,
  y: number,
  width: number,
  height: number,
  gap: number,
) {
  const at = (laid: ReturnType<typeof treemapped>) =>
    laid.map((r) => ({ ...r, x: r.x + x, y: r.y + y }));
  for (const tiling of TILINGS) {
    const laid = treemapped(shares, width, height, gap, tiling);
    if (laid.every(readable)) return at(laid);
  }
  // A short, wide band suits a row better than a treemap.
  const rows = rowsLayout(shares, width, height, gap);
  return rows.every(readable) ? at(rows) : null;
}

/**
 * Lays `shares` (biggest first) out in a box so the biggest sit on the side facing the talk of the
 * town: a plain layout fills from the top left, so it's mirrored across (`flipX`) or up (`flipY`).
 */
function towards(
  shares: readonly SizedTile[],
  x: number,
  y: number,
  width: number,
  height: number,
  gap: number,
  flipX: boolean,
  flipY: boolean,
) {
  const laid = treemapIn(shares, 0, 0, width, height, gap);
  return (
    laid?.map((r) => ({
      ...r,
      x: x + (flipX ? width - r.x - r.w : r.x),
      y: y + (flipY ? height - r.y - r.h : r.y),
    })) ?? null
  );
}

/**
 * A column beside the talk of the town, in two halves whose biggest posts meet in the middle, next
 * to it: size falls away towards the corners.
 */
function sideColumn(
  items: readonly SizedTile[],
  x: number,
  width: number,
  height: number,
  gap: number,
  crownOnLeft: boolean,
) {
  const flipX = !crownOnLeft;
  const up: SizedTile[] = [];
  const down: SizedTile[] = [];
  let upShare = 0;
  let downShare = 0;
  for (const s of items) {
    if (downShare < upShare) {
      down.push(s);
      downShare += s.share;
    } else {
      up.push(s);
      upShare += s.share;
    }
  }
  if (up.length > 0 && down.length > 0) {
    const [[, upH], [downY, downH]] = split([upShare, downShare], height, gap) as [
      [number, number],
      [number, number],
    ];
    const upper = towards(up, x, 0, width, upH, gap, flipX, true);
    const lower = towards(down, x, downY, width, downH, gap, flipX, false);
    if (upper && lower) return [...upper, ...lower];
  }
  return towards(items, x, 0, width, height, gap, flipX, false);
}

/** The talk of the town's shape on a wide board: a little wider than tall. */
const CROWN_ASPECT = 1.3;

/** A post band above or below the talk of the town is at least this tall. */
const CENTRE_BAND_MIN = 90;

/**
 * Wide boards put the talk of the town in the middle: a centre column holding it (with a band of
 * posts above and below when there are enough), and the other posts in columns either side.
 * Every region is sized by the shares it holds, so sizes still follow attention. Null when it can't
 * keep every tile readable.
 */
function centred(
  shares: readonly SizedTile[],
  crown: string,
  width: number,
  height: number,
  gap: number,
  bands = true,
): ReturnType<typeof treemapped> | null {
  const top = shares.find((s) => s.id === crown);
  const rest = shares.filter((s) => s.id !== crown);
  if (!top || rest.length < 2) return null;

  // How much of the board the centre column takes so the crown gets its shape, and what's left
  // for each region to aim at.
  const withBands = bands && rest.length >= 5;
  const column = withBands
    ? Math.min(0.6, Math.max(top.share, Math.sqrt((CROWN_ASPECT * height * top.share) / width)))
    : top.share;
  const aims = withBands
    ? [(1 - column) / 2, (1 - column) / 2, (column - top.share) / 2, (column - top.share) / 2]
    : [(1 - column) / 2, (1 - column) / 2];
  const groups: SizedTile[][] = aims.map(() => []);
  const held = aims.map(() => 0);
  for (const s of rest) {
    // Each post goes where it's furthest from its aim.
    const g = aims.reduce(
      (best, aim, i) => (aim - held[i]! > aims[best]! - held[best]! ? i : best),
      0,
    );
    groups[g]!.push(s);
    held[g]! += s.share;
  }
  if (groups.some((g) => g.length === 0)) {
    return withBands ? centred(shares, crown, width, height, gap, false) : null;
  }

  const [left, right, above, below] = held as [number, number, number?, number?];
  const centre = top.share + (above ?? 0) + (below ?? 0);
  type Spans = [[number, number], [number, number], [number, number]];
  const [[, leftW], [cx, centreW], [rx, rightW]] = split(
    [left, centre, right],
    width,
    gap,
  ) as Spans;
  const out: ReturnType<typeof treemapped> = [];
  const add = (laid: ReturnType<typeof treemapped> | null) => {
    if (!laid) return false;
    out.push(...laid);
    return true;
  };
  if (!add(sideColumn(groups[0]!, 0, leftW, height, gap, false))) return null;
  if (!add(sideColumn(groups[1]!, rx, rightW, height, gap, true))) return null;
  if (withBands) {
    const [[, aboveH], [cy, crownH], [by, belowH]] = split(
      [above!, top.share, below!],
      height,
      gap,
    ) as Spans;
    if (aboveH < CENTRE_BAND_MIN || belowH < CENTRE_BAND_MIN) {
      return centred(shares, crown, width, height, gap, false);
    }
    const aboveRects = towards(groups[2]!, cx, 0, centreW, aboveH, gap, false, true);
    const belowRects = towards(groups[3]!, cx, by, centreW, belowH, gap, false, false);
    if (!aboveRects || !belowRects) return centred(shares, crown, width, height, gap, false);
    add(aboveRects);
    add(belowRects);
    out.push({ id: crown, x: cx, y: cy, w: centreW, h: crownH });
  } else {
    out.push({ id: crown, x: cx, y: 0, w: centreW, h: height });
  }
  // The talk of the town must still be the biggest tile.
  const crownArea = out.find((r) => r.id === crown)!;
  return out.every((r) => r.w * r.h <= crownArea.w * crownArea.h) ? out : null;
}

/**
 * The first arrangement where every tile is readable: on wide boards the talk of the town in the
 * middle, else a plain treemap; rows on narrow ones.
 */
function arrange(
  shares: readonly SizedTile[],
  crown: string | null,
  width: number,
  height: number,
  gap: number,
) {
  if (width >= ROWS_BELOW_PX) {
    const middle = crown ? centred(shares, crown, width, height, gap) : null;
    if (middle) return middle;
    for (const tiling of TILINGS) {
      const laid = treemapped(shares, width, height, gap, tiling);
      if (laid.every(readable)) return laid;
    }
  }
  const rows = rowsLayout(shares, width, height, gap, crown);
  return rows.every(readable) ? rows : null;
}

/**
 * Lays out the posts: area follows the shares from `tileShares` (sublinear, capped, floored, the
 * talk of the town biggest), separated by a `gap`px surface gap. Wide boards are a treemap, in the
 * first arrangement where every tile can show a title; narrow ones (phones), or wide ones where no
 * arrangement can, use rows. Posts that can't get a readable tile (more than the board holds) are
 * left out, the smallest first, and listed in `dropped`.
 */
export function layoutBoard(
  posts: readonly BoardPost[],
  width: number,
  height: number,
  gap = 2,
): BoardLayout {
  if (posts.length === 0 || width <= 0 || height <= 0) {
    return { tiles: [], crown: null, dropped: [] };
  }
  const byId = new Map(posts.map((p) => [p.id, p]));
  const top = talkOfTheTown(posts.map(sizing));
  const crown = top >= 0 ? posts[top]!.id : null;
  // Who gives up their tile first: the smallest, older posts before new ones (they deserve a
  // look), never the talk of the town.
  const share = new Map(tileShares(posts.map(sizing), width * height).map((s) => [s.id, s.share]));
  const dropOrder = posts
    .filter((p) => p.id !== crown)
    .sort(
      (a, b) =>
        share.get(a.id)! - share.get(b.id)! ||
        Number(!!a.fresh) - Number(!!b.fresh) ||
        a.score - b.score ||
        a.createdAt - b.createdAt,
    );

  const fits = maxTilesForArea(width * height);
  for (let drop = Math.max(0, posts.length - fits); ; drop++) {
    const dropped = dropOrder.slice(0, drop);
    const gone = new Set(dropped.map((p) => p.id));
    const shares = sharesFor(posts.filter((p) => !gone.has(p.id)).map(sizing), width, height);
    const last = drop >= dropOrder.length;
    const rects =
      arrange(shares, crown, width, height, gap) ??
      (last ? rowsLayout(shares, width, height, gap, crown) : null);
    if (!rects) continue;
    return {
      tiles: rects.map(({ id, ...rect }) => ({ post: byId.get(id)!, ...rect })),
      crown,
      dropped,
    };
  }
}

export type TileSize = 'xl' | 'lg' | 'md' | 'sm';

/** How much a tile can show: big tiles get the image and full meta, tiny ones just the title. */
export function tileSize(w: number, h: number): TileSize {
  const area = w * h;
  if (area >= 90_000 && w >= 260 && h >= 200) return 'xl';
  if (area >= 36_000 && w >= 180 && h >= 140) return 'lg';
  if (area >= 14_000 && h >= 80) return 'md';
  return 'sm';
}

/** Line height of tile titles, as in styles.css. */
export const TITLE_LINE = 1.22;

/**
 * Room a single word needs, in average characters: the average counts the narrow spaces, so whole
 * words run wider (12-20% in the faces measured). A word can't wrap,
 * so it gets the margin; lines of several words even out.
 */
const WORD_WIDTH = 1.2;

/** Lines `words` take when wrapped at `perLine` (average) characters; Infinity if a word can't fit. */
function wrappedLines(words: readonly string[], perLine: number): number {
  let lines = 1;
  let used = 0;
  for (const word of words) {
    if (word.length * WORD_WIDTH > perLine) return Infinity;
    if (used === 0) used = word.length;
    else if (used + 1 + word.length <= perLine) used += 1 + word.length;
    else {
      lines += 1;
      used = word.length;
    }
  }
  return lines;
}

/**
 * The biggest title size (px, between `min` and `max`) at which `title` fits a `width`×`height`
 * box on at most `maxLines` lines, words unbroken, so headlines fill big tiles like a front page
 * instead of leaving them half empty. `charWidth` is the face's average character width in ems.
 */
export function fitTitle(
  title: string,
  width: number,
  height: number,
  min: number,
  max: number,
  maxLines: number,
  charWidth: number,
): { size: number; lines: number } {
  const words = title.trim().split(/\s+/);
  for (let size = Math.floor(max); size > min; size--) {
    const lines = wrappedLines(words, width / (size * charWidth));
    if (lines <= maxLines && lines * size * TITLE_LINE <= height) return { size, lines };
  }
  const lines = wrappedLines(words, Math.max(1, width / (min * charWidth)));
  return { size: min, lines: Math.max(1, Math.min(lines, maxLines)) };
}

export interface TileParts {
  size: TileSize;
  /** Too small for a readable word: no text (the tooltip and label carry the title). */
  tiny: boolean;
  /** Narrow or small: tighter padding, and a meta line only to carry the AI tag. */
  compact: boolean;
  /** Has a meta line (author and time, or the AI tag). */
  meta: boolean;
  /** Where an AI summary shows its ✦: null only for people's posts and specks too small for it. */
  aiTag: 'meta' | 'alone' | null;
}

/** What a tile of this size shows. */
export function tileParts(post: Pick<BoardPost, 'isAgent'>, w: number, h: number): TileParts {
  const size = tileSize(w, h);
  const tiny = w < 64 || h < 34;
  const compact = size === 'sm' || w < 120;
  const aiTag = !post.isAgent ? null : tiny ? (w >= 44 && h >= 26 ? 'alone' : null) : 'meta';
  return { size, tiny, compact, meta: !compact || post.isAgent, aiTag };
}
